import {
  Flex,
  Image,
  LoadingOverlay,
  SimpleGrid,
  Text,
} from '@mantine/core';
import { Dropzone } from '@mantine/dropzone';
import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import { formatBytes, formatFileSize } from '../../../utils/format';
import IconDropFile from '../../icons/IconDropFile';
import Button from '../Button';
import styles from './styles.scoped.css';
import IcDotPdf from '../../icons/IcDotPdf';
import IcDotCsv from '../../icons/IcDotCsv';
import IcDotDoc from '../../icons/IcDotDoc';
import IconDownload from '../../icons/IconDownload';

export default function ImagePicker({
  title,
  info,
  onSelect,
  maxSize,
  required,
  accept = ['image/jpeg', 'image/png'],
  withDownloadTemplate,
  downloadTemplate,
  IconDownloadTemplate,
  isPixelCheck,
  height,
  width,
  defaultValue,
  disabled = false,
  errorMessage = '',
  setErrorMessage = () => {},
}) {
  const [files, setFiles] = useState(defaultValue || []);
  const [error, setError] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');
  const [isProcessingFile, setIsProcessingFile] = useState(false);

  useEffect(() => {
    let timeoutId;
    const stopProcessing = () => {
      timeoutId = setTimeout(() => setIsProcessingFile(false), 300);
    };

    if (isPixelCheck && files.length > 0) {
      const imageUrl = URL.createObjectURL(files[0]);
      imageSize(imageUrl)
        .then((res) => {
          if (res.width < width || res.height < height) {
            setError(true);
            setFiles([]);
          } else {
            onSelect(files);
            setError(false);
          }
        })
        .finally(stopProcessing);
    } else {
      onSelect(files);
      stopProcessing();
    }

    return () => clearTimeout(timeoutId);
  }, [files]);

  const imageSize = (url) => {
    const img = document.createElement('img');
    const promise = new Promise((resolve, reject) => {
      img.onload = () => {
        // Natural size is the actual image size regardless of rendering.
        // The 'normal' `width`/`height` are for the **rendered** size.
        const width = img.naturalWidth;
        const height = img.naturalHeight;
        // Resolve promise with the width and height
        resolve({ width, height });
      };
      // Reject promise on error
      img.onerror = reject;
    });

    // Setting the source makes it start downloading and eventually call `onload`
    img.src = url;

    return promise;
  };

  const getFileIcon = (file) => {
    const { type } = file;
    const imageUrl = URL.createObjectURL(file);

    if (type.startsWith('image/')) {
      return (
        <Image
          alt="preview"
          height={100}
          imageProps={{ onLoad: () => URL.revokeObjectURL(imageUrl) }}
          src={imageUrl}
          width={100}
        />
      );
    }

    switch (type) {
      case 'application/pdf':
        return <IcDotPdf />;
      case 'text/csv':
      case 'application/vnd.ms-excel':
        return <IcDotCsv />;
      case 'application/msword': // .doc
      case 'application/vnd.openxmlformats-officedocument.wordprocessingml.document': // .docx
        return <IcDotDoc />;
      default:
        return <IcDotDoc />;
    }
  };
  const previews = files.map((file) => {
    return (
      <div className={styles.previews} key={file.name}>
        {getFileIcon(file)}
        <div>
          <span>{file.name}</span>
          {file.size ? <span>{formatFileSize(file.size)}</span> : null}
        </div>
      </div>
    );
  });

  const onReject = (e) => {
    setIsProcessingFile(false);
    setError(true);
    const formatLabels = {
      'application/pdf': '.pdf',
      'application/msword': '.doc',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document': '.docx',
    };

    const formatUser = accept.map(type => {
      if (formatLabels[type]) return formatLabels[type];
      return '.' + type.split('/').pop();
    }).join(', ');
    if (e[0].errors[0].message.includes('type'))
      setErrorMsg(
        `File harus berformat ${formatUser}`
      );
    if (e[0].errors[0].message.includes('larger'))
      setErrorMsg(`File lebih dari ${formatBytes(maxSize, 2, false)}`);
    setErrorMessage('');
  };

  const onDrop = (e) => {
    setIsProcessingFile(true);
    setFiles(e);
    setError(false);
    setErrorMessage('');
    setErrorMsg('');
  };

  return (
    <div className={styles.container}>
      <label>
        {title}
        {required && <span className={styles.withAsterisk}> *</span>}
      </label>
      <SimpleGrid cols={withDownloadTemplate ? 2 : 1}>
        <Dropzone
          accept={accept}
          disabled={disabled}
          maxSize={maxSize}
          onDrop={onDrop}
          onReject={onReject}
          styles={() => ({
            root: {
              minHeight: 120,
              padding: '.5rem',
              backgroundColor: 'var(--mantine-color-body)', // Uses theme variable for light/dark
              position: 'relative',

              // Error state logic
              border: error
                ? `2px dashed var(--mantine-color-red-6)`
                : `2px dashed var(--mantine-color-gray-4)`,

              // Styles for when a file is being hovered/accepted
              '&[data-accept]': {
                borderColor: 'var(--mantine-color-green-6)',
              },
            },
          })}
        >
          <LoadingOverlay
            loaderProps={{ size: 'sm', color: 'var(--green-primary-color)' }}
            overlayProps={{ radius: 'sm', blur: 1 }}
            visible={isProcessingFile}
          />
          <div className={styles.rowContainer}>
            {defaultValue && previews.length === 0 && (
              <Image src={defaultValue} width={100} />
            )}
            {previews.length > 0 ? (
              <SimpleGrid
                breakpoints={[{ maxWidth: 'sm', cols: 1 }]}
                cols={1}
                mt={previews.length > 0 ? 'xl' : 0}
                styles={{
                  root: { placeItems: 'center' }
                }}
              >
                {previews}
                <Button disabled={disabled} outlined>
                  {previews.length > 0 ? 'Ganti' : 'Pilih File'}
                </Button>
              </SimpleGrid>
            ) : (
              <Flex align="center" direction="column" gap="xs">
                <IconDropFile />
                <span>atau</span>
                <Button disabled={disabled} outlined>
                  {previews.length > 0 ? 'Ganti' : 'Pilih File'}
                </Button>
              </Flex>
            )}

          </div>
        </Dropzone>
        {withDownloadTemplate && (
          <div className={styles.downloadContainer}>
            {IconDownloadTemplate}
            <Button
              className={styles.button}
              onClick={downloadTemplate}
              style="primary-500"
              type="button"
            >
              <IconDownload />
              Unduh Template
            </Button>
          </div>
        )}
      </SimpleGrid>
      <Text align="left" c={'dimmed'} fz={11}>
        {info}
      </Text>
      <Text align="left" c={'red'} fz={11}>
        {errorMsg || errorMessage}
      </Text>
    </div>
  );
}

ImagePicker.propTypes = {
  accept: PropTypes.array,
  defaultValue: PropTypes.string,
  disabled: PropTypes.bool,
  display: PropTypes.oneOf(['block', 'flex']),
  downloadTemplate: PropTypes.func,
  errorMessage: PropTypes.string,
  files: PropTypes.array,
  height: PropTypes.number,
  IconDownloadTemplate: PropTypes.node,
  info: PropTypes.string,
  isPixelCheck: PropTypes.bool,
  maxSize: PropTypes.number,
  onSelect: PropTypes.func,
  required: PropTypes.bool,
  setErrorMessage: PropTypes.func,
  title: PropTypes.string,
  width: PropTypes.number,
  withDownloadTemplate: PropTypes.bool,
};
